import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { capitalize } from 'es-toolkit/string';

import { identitiesKeys } from '~/hooks/useLoaders/useIdentities';
import useToast, { type ToastData, ToastStatus } from '~/hooks/useToast';

interface Props {
  subscriberId: string;
}

/**
 * Must be called from a tab-agnostic scope. Hosting inside a tab component
 * silently no-ops when a redirect lands on a different tab (e.g.
 * `?tab=billing&connected=google` from a future OAuth provider).
 *
 * Handles:
 * - `?connected=<provider>` — post-OAuth toast + identities refetch + clear
 * - `?intent=connect` — deeplink that promotes to `?modal=connected_accounts`
 * - `?billing_portal_error=1` — failed Stripe portal mint bounced back here
 */
const useSubscriberPortalRedirectParams = ({ subscriberId }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [toastData, setToastData] = useState<ToastData | undefined>();
  const hasProcessedConnection = useRef<string | null>(null);

  const connectedProvider = searchParams.get('connected');
  const intent = searchParams.get('intent');
  const billingPortalError = searchParams.get('billing_portal_error');

  useToast(toastData);

  useEffect(() => {
    if (connectedProvider && connectedProvider !== hasProcessedConnection.current) {
      hasProcessedConnection.current = connectedProvider;

      const providerName = capitalize(connectedProvider);

      setToastData({
        status: ToastStatus.SUCCESS,
        message: t('connected_accounts_modal.connect_success_toast', {
          providerName,
          defaultValue: `Successfully connected ${providerName} to your subscription`,
        }),
      });

      queryClient.invalidateQueries({ queryKey: identitiesKeys.bySubscriber(subscriberId) });

      setSearchParams((params) => {
        params.delete('connected');
        return params;
      });
    }
  }, [connectedProvider, setSearchParams, queryClient, subscriberId, t]);

  useEffect(() => {
    if (billingPortalError) {
      setToastData({
        status: ToastStatus.ERROR,
        message: t('manage.groups.card.billing_portal_error', {
          defaultValue: "We couldn't open the billing portal. Please try again.",
        }),
      });

      setSearchParams((params) => {
        params.delete('billing_portal_error');
        return params;
      });
    }
  }, [billingPortalError, setSearchParams, t]);

  useEffect(() => {
    if (intent === 'connect') {
      setSearchParams((params) => {
        params.set('modal', 'connected_accounts');
        params.delete('intent');
        return params;
      });
    }
  }, [intent, setSearchParams]);
};

export default useSubscriberPortalRedirectParams;
